<template>
    <div
        class="details-container"
        @click.stop="$emit('close')"
    >
        <div
            class="d-flex flex-column multi-trip-details-container-inner"
            style="background: #FFFFFF; height: 95vh; overflow: hidden; border-radius: .5em; width: 780px;"
            @click.stop=""
        >
            <header class="flex-shrink-0 py-3">
                <div
                    class="col-12 mb-2 pb-2"
                    style="border-bottom: #d9d9d9 1px solid"
                >
                    <div class="d-flex justify-content-end flex-wrap">
                        <input
                            id="showAllPhotosCheckboxMulti"
                            v-model="showAllPhotos"
                            type="checkbox"
                            class="form-check-input"
                        >
                        <span class="ms-2 me-4">{{ $t('observations.show_all_photos') }}</span>
                        <button
                            class="btn btn-sm btn-secondary mx-1"
                            @click.stop="$emit('close')"
                        >
                            {{ $t('common.close') }}
                        </button>
                        <button
                            class="btn btn-sm btn-primary mx-1"
                            :disabled="pdfLoading"
                            @click.stop="generatePdf"
                        >
                            {{ $t('common.print') }}
                        </button>
                    </div>
                </div>
            </header>
            <main
                ref="mainContent"
                class="flex-grow-1 overflow-auto p-3"
                style="overflow-x: hidden !important;"
            >
                <div class="print-page">
                    <h1 class="report-header px-2">
                        {{ $t('trip.trip_with_observations_multi_pdf_title') }}
                    </h1>
                    <!-- Shared summary map for all selected trips -->
                    <div class="col-12">
                        <div class="map-container summary-map-container">
                            <map-container
                                ref="mapContainer"
                                :find-user="false"
                                @on-map-changed="mapChanged"
                            />
                        </div>
                    </div>
                    <!-- Combined trip summary data -->
                    <span class="span-title px-2 mt-3">{{ $t('work_report.trips') }}</span>
                    <vue3-datatable
                        v-if="tableTripItems.length > 0"
                        :rows="tableTripItems"
                        :columns="tripFields"
                        :pagination="false"
                        :sticky-header="true"
                        height="100%"
                        :sortable="true"
                        skin="bh-table-compact"
                        row-class="routa-table-row"
                        cell-class="routa-table-cell"
                        class="my-2"
                    >
                        <template #start_time="row">
                            <span>{{ toLocalTime(row.value.start_time) }}</span>
                        </template>
                        <template #end_time="row">
                            <span>{{ toLocalTime(row.value.end_time) }}</span>
                        </template>
                        <template #avg_speed="row">
                            <span>{{ getAverageSpeed(row.value.length, getDurationInMinutes(row.value.start_time, row.value.end_time)) }}</span>
                        </template>
                    </vue3-datatable>
                    <!-- Combined observation summary figures -->
                    <span class="span-title px-2 mt-3">{{ $t('menu.observations') }}</span>
                    <vue3-datatable
                        v-if="tableItems.length > 0"
                        :rows="tableItems"
                        :columns="fields"
                        :pagination="false"
                        :sticky-header="true"
                        height="100%"
                        :sortable="true"
                        skin="bh-table-compact"
                        row-class="routa-table-row"
                        cell-class="routa-table-cell"
                        class="my-2"
                    />
                    <!-- Weather data per trip -->
                    <span class="span-title px-2 mt-3">{{ $t('weather_station.data_title') }}</span>
                    <table
                        v-if="weatherRows.length > 0"
                        class="table table-sm mb-3 mx-2"
                    >
                        <thead>
                            <tr>
                                <th>{{ $t('trip_list.driver') }}</th>
                                <th>{{ $t('contracts.external.station') }}</th>
                                <th>{{ $t('contracts.external.time') }}</th>
                                <th>{{ $t('contracts.external.air_temp') }}</th>
                                <th>{{ $t('contracts.external.road_temp') }}</th>
                                <th>{{ $t('contracts.external.avg_wind') }}</th>
                                <th>{{ $t('contracts.external.rain_type') }}</th>
                                <th>{{ $t('contracts.external.rain_amount') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in weatherRows" :key="row.tripId">
                                <td>{{ row.driver }}</td>
                                <td>{{ row.weatherItem ? row.weatherItem.station : '-' }}</td>
                                <td>{{ row.weatherItem ? row.weatherItem.time : '-' }}</td>
                                <td>{{ row.weatherItem ? row.weatherItem.airTemp : '-' }}</td>
                                <td>{{ row.weatherItem ? row.weatherItem.roadTemp : '-' }}</td>
                                <td>{{ row.weatherItem ? row.weatherItem.windMS : '-' }}</td>
                                <td>{{ row.weatherItem ? row.weatherItem.rainType : '-' }}</td>
                                <td>{{ row.weatherItem ? row.weatherItem.rainSum : '-' }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div
                        v-if="weatherRows.length === 0"
                        class="col-12 p-2 mt-2"
                    >
                        {{ fetchLoading ? $t('common.loading') : $t('trip_list.no_weather_data') }}
                    </div>
                    <div class="col-12 px-2">
                        <span class="span-title">{{ $t('velho_equipment.additional_info') }}</span>
                        <textarea
                            v-model="additionalInfo"
                            class="form-control"
                            rows="4"
                        />
                    </div>
                </div>
                <observation-print-details
                    v-for="observation in allObservations"
                    ref="observations"
                    :key="observation.id"
                    :observation="observation"
                    :report-completed="reportCompleted"
                    :show-all-photos="showAllPhotos"
                    @remove="removeObservation"
                />
            </main>
            <footer class="flex-shrink-0 p-3 border-top">
                <div class="d-flex justify-content-between">
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        @click.stop="scrollToTop"
                    >
                        ↑
                    </button>

                    <button
                        class="btn btn-sm btn-outline-secondary"
                        @click.stop="scrollToBottom"
                    >
                        ↓
                    </button>
                </div>
            </footer>
        </div>
        <div
            v-if="fetchLoading || pdfLoading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import {vehicleHelper} from "@/components/mixins/VehicleMixin";
import MapContainer from "@/components/map/MapContainer";
import {geomTypes} from "@/components/mixins/ObservationMixin";
import {restApi} from "@/components/mixins/RestApiMixin";
import {observationHelper} from "@/components/mixins/ObservationMixin";
import {mapHelper} from "@/components/mixins/MapMixin";
import {imageCacheService} from "@/services/ImageCacheService";
import ObservationPrintDetails from "@/components/observation/ObservationPrintDetails";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import {pdfMixin} from "@/components/mixins/PdfMixin";
import {weatherHelper} from "@/components/mixins/WeatherMixin.js";
import {PdfGeneratorManager} from "@/modules/PdfGeneratorManager";
import {inject} from "vue";
import obsSvg from "@/assets/observation.svg";

export default {
    name: "MultiTripWithObservationsReport",
    components: {MapContainer, ObservationPrintDetails, Vue3Datatable},
    mixins: [timeUtils, restApi, observationHelper, mapHelper, vehicleHelper, pdfMixin, weatherHelper],
    props: {
        trips: {
            type: Array,
            default: () => []
        },
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject("showAlert");
        return {showAlert};
    },
    data() {
        return {
            icon: null,
            map: null,
            tripRecords: {},
            reportCompleted: false,
            showAllPhotos: false,
            additionalInfo: "",
            pdfLoading: false,
            fields: [
                {field: "name", title: this.$t("road_reports.type")},
                {field: "count", title: this.$t("road_reports.count")}
            ],
            tripFields: [
                {field: "driver", title: this.$t("trip_list.driver"), sort: false},
                {field: "contract", title: this.$t("orders.contract"), sort: false},
                {field: "order", title: this.$t("vehicle_position.order"), sort: false},
                {field: "start_time", title: this.$t("trip_list.start_time"), sort: false},
                {field: "end_time", title: this.$t("trip_list.end_time"), sort: false},
                {field: "length", title: this.$t("trip_list.trip_length"), sort: false},
                {field: "avg_speed", title: this.$t("trip_list.avg_speed"), sort: false}
            ],
            items: [],
            tableItems: [],
            allObservations: [],
        }
    },
    computed: {
        tableTripItems() {
            return this.trips
        },
        weatherRows() {
            return this.trips
                .filter(trip => this.tripRecords[trip.id] && this.tripRecords[trip.id].weatherItem)
                .map(trip => ({
                    tripId: trip.id,
                    driver: trip.driver,
                    weatherItem: this.tripRecords[trip.id].weatherItem
                }))
        },
        fetchLoading() {
            return this.trips.some(trip => {
                const record = this.tripRecords[trip.id]
                return !record || record.loading > 0
            })
        }
    },
    beforeUnmount() {
        imageCacheService.clear();
    },
    methods: {
        async fetchWeatherDataForTrip(trip) {
            const record = this.tripRecords[trip.id]
            record.loading++
            const params = {
                time: trip.start_time,
                lat: trip.geometry[0].y,
                lon: trip.geometry[0].x
            }
            this.restFetchParams(this.weatherDataUrl, params, (response) => {
                record.loading--
                if (response && response.data) {
                    record.weatherItem = this.getWeatherDataItem(response.data)
                }
            })
        },

        async fetchObservationsForTrip(trip) {
            const record = this.tripRecords[trip.id]
            try {
                const observationsData = await new Promise((resolve, reject) => {
                    record.loading++
                    this.restFetchParams(this.observationUrl, {
                        from: trip.start_time,
                        to: trip.end_time,
                        reporter: trip.driver_id,
                        contract: trip.contract_id,
                    }, (response) => {
                        record.loading--
                        resolve(response.data)
                    }, (error) => {
                        record.loading--
                        reject(error)
                    })
                })
                record.observations = observationsData
                this.drawTripOnMap(trip)
                this.drawObservationsForTrip(trip)
                // eslint-disable-next-line no-empty
            } catch (error) {}
        },

        async initView() {
            if (!this.map) return
            this.map.removeMapItemsByType(this.OBSERVATION)
            this.map.removeMapItemsByType(this.TRACE)

            // Set up a tracking record per trip and kick off independent fetches
            this.trips.forEach(trip => {
                if (!this.tripRecords[trip.id]) {
                    this.tripRecords[trip.id] = {
                        trip,
                        observations: [],
                        weatherItem: null,
                        loading: 0
                    }
                }
            })

            await Promise.all(this.trips.map(trip => Promise.all([
                this.fetchObservationsForTrip(trip),
                this.fetchWeatherDataForTrip(trip)
            ])))

            this.initItems()

            this.map.zoomToGroup(this.TRACE)
        },

        drawTripOnMap(trip) {
            if (trip && trip.geometry) {
                const color = "#565659"
                const dash = false
                const arrow = false
                const width = 3
                this.map.drawPolyLine(trip.id, this.TRACE, trip.geometry, color, dash, arrow, width, 1, false, false, null, false)
            }
        },

        drawObservationsForTrip(trip) {
            const record = this.tripRecords[trip.id]
            if (!record) return
            for (const observation of record.observations) {
                if (observation.type.geometry_type === geomTypes.POINT) {
                    this.map.showMapMarker(observation.id, this.OBSERVATION, observation.geometry.point.y,
                        observation.geometry.point.x, this.icon, false, 0)
                } else if (observation.type.geometry_type === geomTypes.LINESTRING) {
                    const color = observation.closed_time ? "#bcbcbc" : "#bb330e";
                    const points = observation.geometry.line_string.points.map(point => {
                        return {x: point[0], y: point[1]}
                    });
                    this.map.drawPolyLine(observation.id, this.OBSERVATION, points, color, false, false, 3, 1, true)
                }
            }
        },

        redrawAllObservations() {
            this.map.removeMapItemsByType(this.OBSERVATION)
            this.trips.forEach(trip => this.drawObservationsForTrip(trip))
        },

        initItems() {
            const table = []
            const pdf = []
            const observations = []
            this.trips.forEach(trip => {
                const record = this.tripRecords[trip.id]
                if (record) {
                    observations.push(...record.observations)
                }
            })
            this.allObservations = observations

            const ids = observations.map(ob => ob.type.id)
            const uniqueObservationsByType = observations.filter((observation, index, self) =>
                index === self.findIndex((o) => o.type.id === observation.type.id)
            )
            uniqueObservationsByType.forEach(obs => {
                const count = ids.filter(id => id === obs.type.id).length
                pdf.push([obs.type.name, count])
                table.push({
                    id: obs.type.id,
                    name: obs.type.name,
                    count: count
                })
            })
            table.sort((a, b) => a.name.localeCompare(b.name));
            this.items = pdf
            this.tableItems = table
        },

        observationDetails(obs) {
            return [
                [this.$t("observations.id"), obs.id],
                [this.$t("observations.created_time"), this.toLocalTime(obs.created_time)],
                [this.$t("contracts.name"), obs.contract ? obs.contract.name : ""],
                [this.$t("contracts.customer"), obs.contract ? obs.contract.customer.name : ""],
                [this.$t("observations.order"), obs.work_order && obs.work_order.task_type ? obs.work_order.task_type.name : ""],
                [this.$t("observations.status"), obs.closed_time ? this.$t("observations.closed") : this.$t("observations.open")],
                [this.$t("observations.location"), this.getStreetAddressString(obs) + (obs.city ? " " + obs.city.name : "")],
            ]
        },

        removeObservation(observationId) {
            for (const trip of this.trips) {
                const record = this.tripRecords[trip.id]
                if (!record) continue
                const index = record.observations.findIndex(item => item.id === observationId)
                if (index >= 0) {
                    record.observations.splice(index, 1)
                    this.redrawAllObservations()
                    this.initItems()
                    break
                }
            }
        },

        isPng(photo) {
            return photo.startsWith("data:image/png;");
        },

        async takePhotoOfMap(index) {
            const result = this.$refs.observations[index].getMarkerEditorMap()
            const ss = await result.takeScreenShot()
            return await new Response(ss).arrayBuffer();
        },

        async getMapScreenshotAsArrayBuffer() {
            const screenshot = await this.map.takeScreenShot()
            return await new Response(screenshot).arrayBuffer()
        },

        async fetchPhotos(observation, fetchAll = false) {
            const baseUrl = this.observationPhotoUrl;
            const id = observation.id;
            const results = [];

            if (!observation.photos || observation.photos.length === 0) {
                return results;
            }

            const photosToFetch = fetchAll ? observation.photos : [observation.photos[0]];

            const getPhoto = async (photo) => {
                const photoId = photo.id.toString();
                if (imageCacheService.has(photoId)) {
                    return imageCacheService.get(photoId);
                } else {
                    const url = `${baseUrl}/${id}/${photo.id}`;
                    const response = await new Promise(resolve => this.restFetch(url, resolve));
                    return response.data;
                }
            };

            for (const photo of photosToFetch) {
                results.push(await getPhoto(photo));
            }

            return results;
        },

        mapChanged(map) {
            this.map = map
            this.icon = this.map.getMarkerStore().getSvgMarker(obsSvg, null, null, "2em", "2em")
            this.initView()
        },

        async generatePdf() {
            this.pdfLoading = true
            try {
                const {
                    pdfDoc, pdfService, fonts, colors, footerLogo, companyFooterLogo, pdfGeneratorManager
                } = await this.initializePdf()

                const screenshot = await this.getMapScreenshotAsArrayBuffer()
                let nextYPosition = await this.addPdfHeaderWithMap(this.$t("trip.trip_with_observations_multi_pdf_title"), screenshot)

                // Combined trip data
                let headers = []
                this.tripFields.forEach(item => headers.push(item.title))
                const tripData = [
                    headers,
                    ...this.tableTripItems.map(trip => {
                        return [
                            trip.driver,
                            trip.contract,
                            trip.order,
                            this.toLocalTime(trip.start_time),
                            this.toLocalTime(trip.end_time),
                            trip.length,
                            this.getAverageSpeed(trip.length, this.getDurationInMinutes(trip.start_time, trip.end_time))
                        ]
                    })
                ]
                const tablePage = pdfDoc.getPage(pdfDoc.getPages().length - 1)
                nextYPosition -= 15

                nextYPosition = pdfService.addText(pdfDoc, this.$t("work_report.trips"), fonts.fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo)
                nextYPosition = pdfService.addTable(tripData, pdfDoc, {
                    textFont: fonts.fontText,
                    headerFont: fonts.fontBold,
                    textFontSize: 8,
                    headerFontSize: 9,
                    textColor: colors.textColor,
                    headerFontColor: colors.boldColor
                }, nextYPosition, undefined, undefined, footerLogo, undefined, undefined, undefined, companyFooterLogo)
                nextYPosition -= 25

                // Combined observation summary data
                nextYPosition = pdfService.addText(pdfDoc, this.$t("menu.observations"), fonts.fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo)
                nextYPosition = pdfService.addTable([[this.$t("road_reports.type"), this.$t("road_reports.count")], ...this.items], pdfDoc, {
                    textFont: fonts.fontText,
                    headerFont: fonts.fontBold,
                    textFontSize: 9,
                    headerFontSize: 9,
                    textColor: colors.textColor,
                    headerFontColor: colors.boldColor
                }, nextYPosition, undefined, undefined, footerLogo, undefined, undefined, undefined, companyFooterLogo)

                // Weather data per trip
                nextYPosition -= 25
                const addNewPage = nextYPosition - 200 < pdfService.footerZone
                if (addNewPage) {
                    nextYPosition -= 500
                }
                nextYPosition = pdfService.addText(pdfDoc, this.$t("weather_station.data_title"), fonts.fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo)
                if (this.weatherRows.length > 0) {
                    const weatherHeaders = [this.$t("trip_list.driver"), this.$t("contracts.external.station"), this.$t("contracts.external.time"), this.$t("contracts.external.air_temp"), this.$t("contracts.external.road_temp"), this.$t("contracts.external.avg_wind"), this.$t("contracts.external.rain_type"), this.$t("contracts.external.rain_amount")]
                    const weatherData = [
                        weatherHeaders,
                        ...this.weatherRows.map(row => [row.driver, row.weatherItem.station, row.weatherItem.time, row.weatherItem.airTemp, row.weatherItem.roadTemp, row.weatherItem.windMS, row.weatherItem.rainType, row.weatherItem.rainSum])
                    ]
                    nextYPosition = pdfService.addTable(weatherData, pdfDoc, {
                        textFont: fonts.fontText,
                        headerFont: fonts.fontBold,
                        textFontSize: 9,
                        headerFontSize: 9,
                        textColor: colors.textColor,
                        headerFontColor: colors.boldColor
                    }, nextYPosition, undefined, undefined, footerLogo, undefined, undefined, undefined, companyFooterLogo)
                } else {
                    nextYPosition = pdfService.addText(pdfDoc, this.$t("trip_list.no_weather_data"), fonts.fontText, 10, nextYPosition, undefined, undefined, undefined, footerLogo)
                }

                nextYPosition -= 25

                // Additional info
                if (this.additionalInfo) {
                    nextYPosition = pdfService.addText(pdfDoc, this.$t("velho_equipment.additional_info"), fonts.fontBold, 12, nextYPosition, undefined, undefined, undefined, footerLogo)
                    this.additionalInfo.split("\n").forEach(line => {
                        if (line) {
                            nextYPosition = pdfService.addText(pdfDoc, line, fonts.fontText, 12, nextYPosition, undefined, undefined, 30, footerLogo, companyFooterLogo, colors.textColor)
                        } else {
                            nextYPosition -= 2
                        }
                    })
                }

                const afterTablePage = pdfDoc.getPage(pdfDoc.getPages().length - 1)
                if (afterTablePage === tablePage) {
                    nextYPosition -= 500
                }

                nextYPosition -= 10

                const observationsForPdf = JSON.parse(JSON.stringify(this.allObservations)).sort((a, b) => {
                    return a.photos.length - b.photos.length
                })

                for (let i = 0; i < observationsForPdf.length; i++) {
                    const observation = this.allObservations[i]
                    const photos = await this.fetchPhotos(observation, this.showAllPhotos)
                    nextYPosition = await this.addObservationToPdf(observation, photos, nextYPosition, i)
                }

                const pdfBytes = await pdfGeneratorManager.generateBytesFromPdfDoc(pdfDoc)
                PdfGeneratorManager.downloadPdf(pdfBytes, this.$t("trip.trip_with_observations_multi_pdf_title"))
            } catch (e) {
                this.showAlert(this.$t("common.report_generation_failed"))
            } finally {
                this.pdfLoading = false
            }
        },

        scrollToTop() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({top: 0, behavior: "smooth"});
            }
        },

        scrollToBottom() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({top: container.scrollHeight, behavior: "smooth"});
            }
        }
    }
}
</script>

<style scoped>
.report-header {
    margin: 0;
    padding-top: .2em;
    font-size: 1.2em;
    font-weight: bold;
    text-transform: uppercase;
}

.summary-map-container {
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 1em;
    overflow: hidden;
    width: 750px;
    height: 350px;
    margin: auto;
    margin-bottom: .5em;
}

.multi-trip-details-container-inner {
    width: 95%;
    max-width: 1100px;
    margin: auto;
}

@media (max-width: 768px) {
    .multi-trip-details-container-inner {
        width: 100%;
        border-radius: 0;
    }
}
</style>
